export function chooseCatalogVariant(variants, currentOptions, index, value) {
  const matches = variants.filter(variant => variant.options[index] === value);
  const sameOtherOptions = variant => variant.options.every((option, position) =>
    position === index || !currentOptions[position] || currentOptions[position] === option);
  return matches.find(variant => variant.available && sameOtherOptions(variant))
    || matches.find(variant => variant.available)
    || matches.find(sameOtherOptions) || matches[0] || null;
}

export function catalogOptionAvailable(variants, index, value, currentOptions = [], contextIndexes = []) {
  return variants.some(variant => variant.available && variant.options[index] === value
    && contextIndexes.every(position => position === index || !currentOptions[position]
      || variant.options[position] === currentOptions[position]));
}

export function catalogVariantUrl(href, variantId, origin) {
  if (!/^\d+$/.test(String(variantId))) return href;
  try {
    const url = new URL(href, origin);
    if (url.origin !== origin || !url.pathname.includes('/products/')) return href;
    url.searchParams.set('variant', variantId);
    return url.pathname + url.search + url.hash;
  } catch { return href; }
}

if (typeof HTMLElement !== 'undefined' && !customElements.get('catalog-card-options')) {
  class CatalogCardOptions extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try { this.data = JSON.parse(this.querySelector('[data-catalog-variants]').textContent); }
      catch { return; } // Native option links still work without enhancement.
      this.variants = this.data.variants;
      if (!Array.isArray(this.variants) || !this.variants.length) return;
      this.card = this.closest('product-card');
      if (!this.card) return;
      this.currentOptions = this.variants.find(variant => variant.id === this.data.initialVariant)?.options || [];
      this.groups = [...this.querySelectorAll('[data-catalog-group]')];
      this.indexes = this.groups.map(group => Number(group.dataset.optionIndex));
      this.links = [...this.card.querySelectorAll('[data-product-card-link], .product-grid--price > a')]
        .map(link => [link, link.getAttribute('href')]);
      this.image = this.card.querySelector('img[data-product-card-image]');
      this.originalImage = this.image ? Object.fromEntries(['src','srcset','alt'].map(name => [name,this.image.getAttribute(name)])) : null;
      this.handleClick = this.handleClick.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.overlay = this.querySelector('[data-catalog-overlay]');
      this.trigger = this.overlay?.querySelector('summary');
      this.panel = this.querySelector('[data-catalog-panel]');
      this.status = this.querySelector('[data-catalog-status]');
      this.mobileMedia = matchMedia('(max-width: 1024px), (hover: none), (pointer: coarse)');
      this.handleModeChange = () => { this.closeOverlay(); this.updateTriggerMode(); };
      this.mobileMedia.addEventListener('change', this.handleModeChange);
      this.updateTriggerMode();
      this.handleOverlayToggle = this.handleOverlayToggle.bind(this);
      this.handleOutsidePointer = this.handleOutsidePointer.bind(this);
      this.handleOtherOverlay = this.handleOtherOverlay.bind(this);
      this.handleOverlayEscape = this.handleOverlayEscape.bind(this);
      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeyDown);
      this.overlay?.addEventListener('toggle', this.handleOverlayToggle);
      document.addEventListener('pointerdown', this.handleOutsidePointer);
      document.addEventListener('catalog-options:opened', this.handleOtherOverlay);
      document.addEventListener('keydown', this.handleOverlayEscape);
      this.updateAvailability();
      this.resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => {
        const width = this.getBoundingClientRect().width;
        if (width === this.lastWidth) return;
        this.lastWidth = width;
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => { this.collapseGroups(); this.positionPanel(); });
      }) : null;
      this.resizeObserver?.observe(this);
      this.collapseGroups();
    }

    handleOverlayToggle() {
      this.trigger.setAttribute('aria-expanded', String(this.overlay.open));
      if (!this.overlay.open) { this.closeMobileSheet(); return; }
      document.dispatchEvent(new CustomEvent('catalog-options:opened', { detail: this }));
      if (this.useMobileSheet()) this.openMobileSheet();
      this.collapseGroups();
      this.positionPanel();
    }

    useMobileSheet() {
      return this.mobileMedia.matches && typeof HTMLDialogElement !== 'undefined'
        && typeof HTMLDialogElement.prototype.showModal === 'function'
        && !!this.querySelector('[data-catalog-sheet-template]');
    }

    updateTriggerMode() {
      if (!this.trigger || !this.panel) return;
      this.trigger.setAttribute('aria-controls', this.panel.id + (this.useMobileSheet() ? '-sheet' : ''));
      if (this.useMobileSheet()) this.trigger.setAttribute('aria-haspopup', 'dialog');
      else this.trigger.removeAttribute('aria-haspopup');
    }

    openMobileSheet() {
      if (this.sheet?.open) return;
      if (!this.sheet) {
        this.append(this.querySelector('[data-catalog-sheet-template]').content.cloneNode(true));
        this.sheet = this.querySelector('.catalog-sheet');
        this.sheet.id = this.panel.id + '-sheet';
        this.sheet.addEventListener('cancel', event => { event.preventDefault(); this.closeOverlay(); });
        this.sheet.addEventListener('close', () => {
          if (!this.sheet.open && this.sheetScroll) this.closeOverlay();
        });
      }
      this.sheet.querySelector('[data-catalog-sheet-options]').append(this.panel);
      this.sheet.append(this.status);
      this.updateMobileSheet(this.selectedVariant);
      this.sheetScroll = {
        x: window.scrollX, y: window.scrollY,
        styles: ['position', 'top', 'left', 'width', 'overflow'].map(name =>
          [name, document.body.style.getPropertyValue(name), document.body.style.getPropertyPriority(name)])
      };
      Object.assign(document.body.style, {
        position: 'fixed', top: `-${this.sheetScroll.y}px`, left: `-${this.sheetScroll.x}px`, width: '100%', overflow: 'hidden'
      });
      this.sheet.showModal();
      this.sheet.querySelector('[data-catalog-sheet-close]').focus({ preventScroll: true });
    }

    closeMobileSheet(restoreFocus = true) {
      if (!this.sheetScroll) return;
      const saved = this.sheetScroll;
      this.sheetScroll = null;
      if (this.sheet.open) this.sheet.close();
      this.overlay.append(this.panel);
      this.append(this.status);
      saved.styles.forEach(([name, value, priority]) => value
        ? document.body.style.setProperty(name, value, priority) : document.body.style.removeProperty(name));
      const root = document.documentElement;
      const behavior = [root.style.getPropertyValue('scroll-behavior'), root.style.getPropertyPriority('scroll-behavior')];
      root.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(saved.x, saved.y);
      if (behavior[0]) root.style.setProperty('scroll-behavior', ...behavior);
      else root.style.removeProperty('scroll-behavior');
      if (restoreFocus && this.trigger.isConnected) this.trigger.focus({ preventScroll: true });
    }

    closeOverlay(restoreFocus = true) {
      if (this.overlay) this.overlay.open = false;
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.closeMobileSheet(restoreFocus);
    }

    updateMobileSheet(variant) {
      if (!this.sheet) return;
      const image = this.sheet.querySelector('[data-catalog-sheet-image]');
      const src = variant?.image || this.image?.src || this.image?.currentSrc;
      if (image && src) image.src = src;
      this.sheet.querySelector('[data-catalog-sheet-price]').textContent = variant?.priceText
        || this.card.querySelector('.product-grid--price')?.innerText.replace(/\s+/g, ' ').trim() || '';
      const stock = this.sheet.querySelector('[data-catalog-sheet-availability]');
      stock.hidden = variant ? variant.available : this.variants.some(item => item.available);
      stock.textContent = stock.hidden ? '' : this.dataset.soldOut;
    }

    handleOutsidePointer(event) {
      if (this.sheet?.open) {
        const rect = this.sheet.getBoundingClientRect();
        this.backdropPointerDown = event.target === this.sheet
          && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom);
        return;
      }
      if (this.overlay?.open && !this.contains(event.target)) this.closeOverlay(false);
    }

    handleOtherOverlay(event) {
      if (event.detail !== this && this.overlay?.open) this.closeOverlay(false);
    }

    handleOverlayEscape(event) {
      if (event.key !== 'Escape' || !this.overlay?.open) return;
      event.preventDefault();
      event.stopPropagation();
      this.closeOverlay();
      this.trigger.focus({ preventScroll: true });
    }

    positionPanel() {
      if (!this.overlay?.open || !this.panel || this.sheet?.open) return;
      const trigger = this.trigger.getBoundingClientRect();
      const wanted = Math.min(180, this.panel.scrollHeight + 2);
      const topBoundary = [...document.querySelectorAll('.site-header, .mobile-nav-bar-wrapper, .search-filter-toolbar')]
        .reduce((boundary, element) => {
          const rect = element.getBoundingClientRect();
          return rect.height > 0 && rect.top < 160 && rect.bottom > 0 ? Math.max(boundary, rect.bottom + 8) : boundary;
        }, 8);
      const above = Math.max(0, trigger.top - topBoundary);
      const below = Math.max(0, innerHeight - trigger.bottom - 64);
      const up = above >= wanted || (below < wanted && above > below);
      this.overlay.dataset.direction = up ? 'up' : 'down';
      this.style.setProperty('--catalog-panel-height', `${Math.max(64, Math.min(180, (up ? above : below) - 6))}px`);
    }

    handleKeyDown(event) {
      if (event.key !== ' ' || !event.target.matches('a[data-catalog-choice]')) return;
      event.preventDefault();
      event.target.click();
    }

    handleClick(event) {
      // The search filter's document click handler closes details outside its target.
      // Sheet choices live outside our details; keep their clicks inside this component.
      if (this.sheet?.open && this.sheet.contains(event.target)) event.stopPropagation();
      if (event.target.closest('[data-catalog-sheet-close]')) {
        event.preventDefault();
        this.closeOverlay();
        return;
      }
      if (event.target === this.sheet && this.backdropPointerDown) {
        const rect = this.sheet.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeOverlay();
        this.backdropPointerDown = false;
        return;
      }
      const more = event.target.closest('[data-catalog-more]');
      if (more) {
        event.preventDefault();
        const group = more.closest('[data-catalog-group]');
        group.dataset.expanded = group.dataset.expanded === 'true' ? 'false' : 'true';
        this.collapseGroups();
        this.positionPanel();
        return;
      }
      const choice = event.target.closest('[data-catalog-choice]');
      if (!choice || choice.hasAttribute('data-linked-product') || event.button > 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (choice.getAttribute('aria-disabled') === 'true') return;
      const variant = chooseCatalogVariant(this.variants, this.currentOptions, Number(choice.dataset.optionIndex), choice.dataset.value);
      if (!variant) return;
      this.currentOptions = variant.options;
      this.hasSelection = true;
      this.updateAvailability();
      this.updateCard(variant);
      this.querySelector('[data-catalog-status]').textContent = `${variant.options.join(' / ')}. ${variant.available ? variant.priceText : this.dataset.soldOut}.`;
    }

    updateAvailability() {
      this.groups.forEach(group => {
        const index = Number(group.dataset.optionIndex);
        const isColor = group.dataset.optionKind === 'color';
        const context = this.hasSelection && !isColor ? this.indexes : [];
        group.querySelectorAll('[data-catalog-choice]:not([data-linked-product])').forEach(choice => {
          const available = catalogOptionAvailable(this.variants, index, choice.dataset.value, this.currentOptions, context);
          choice.classList.toggle('is-unavailable', !available);
          choice.setAttribute('aria-disabled', String(!available && !isColor));
          choice.setAttribute('aria-pressed', String(!!this.hasSelection && this.currentOptions[index] === choice.dataset.value));
          choice.title = `${choice.dataset.value}${available ? '' : ' — ' + this.dataset.soldOut}`;
          const label = choice.querySelector('[data-catalog-choice-status]');
          if (label) label.textContent = available ? '' : ` (${this.dataset.soldOut})`;
        });
        const selected = group.querySelector('[data-catalog-selected]');
        selected.textContent = this.hasSelection ? this.currentOptions[index] : '';
        selected.title = selected.textContent;
      });
    }

    updateCard(variant) {
      this.selectedVariant = variant;
      this.card.dataset.catalogVariantSelected = variant.id;
      this.links.forEach(([link, href]) => link.setAttribute('href', catalogVariantUrl(href, variant.id, location.origin)));
      if (!this.image || !this.card.contains(this.image)) {
        const template = [...this.card.querySelectorAll('template[data-deferred-image]')]
          .find(template => template.content.querySelector('img[data-product-card-image]'));
        if (template) template.replaceWith(template.content.cloneNode(true));
        this.image = this.card.querySelector('img[data-product-card-image]');
        if (this.image) this.originalImage = Object.fromEntries(['src','srcset','alt'].map(name => [name,this.image.getAttribute(name)]));
      }
      if (this.image) {
        if (variant.image) {
          this.image.removeAttribute('srcset');
          this.image.src = variant.image;
          this.image.alt = variant.imageAlt || `${this.data.title} — ${variant.options.join(' / ')}`;
        } else {
          Object.entries(this.originalImage).forEach(([name,value]) => value === null ? this.image.removeAttribute(name) : this.image.setAttribute(name,value));
        }
      }
      const price = this.card.querySelector('.product-grid--price > a, .product-grid--price');
      if (price) {
        const nodes = [];
        if (variant.compareAt > variant.price) {
          const previous = document.createElement('s');
          previous.className = 'price-line-through';
          previous.setAttribute('aria-label', `${this.dataset.regularPrice} ${variant.compareAtText}`);
          previous.textContent = variant.compareAtText;
          nodes.push(previous, document.createTextNode(' '));
        }
        const current = document.createElement('span');
        current.className = 'money';
        current.textContent = variant.priceText;
        nodes.push(current);
        if (variant.unitText) {
          const unit = document.createElement('div');
          unit.className = 'unit-price';
          unit.textContent = variant.unitText.replace(/\s+/g, ' ').trim();
          nodes.push(unit);
        }
        price.replaceChildren(...nodes);
      }
      this.card.querySelectorAll('theme-modal[data-is-quick-view]').forEach(modal => {
        const href = modal.getAttribute('data-product-url');
        const next = catalogVariantUrl(this.links[0]?.[1] || href, variant.id, location.origin);
        if (next !== href) {
          modal.setAttribute('data-product-url', next);
          modal.productUrl = next;
          modal._prefetchedModalHTML = null;
        }
      });
      this.updateMobileSheet(variant);
    }

    collapseGroups() {
      this.groups.forEach(group => {
        const row = group.querySelector('[data-catalog-choices]');
        const choices = [...row.querySelectorAll('[data-catalog-choice]')];
        const more = row.querySelector('[data-catalog-more]');
        choices.forEach(choice => { choice.hidden = false; });
        more.hidden = true;
        if (this.sheet?.open) return;
        const availableWidth = row.getBoundingClientRect().width;
        if (!availableWidth) return;
        const widths = choices.map(choice => choice.getBoundingClientRect().width);
        const total = widths.reduce((sum,width) => sum + width, 0) + Math.max(0, choices.length - 1) * 4;
        if (total <= availableWidth) return;
        more.hidden = false;
        const expanded = group.dataset.expanded === 'true';
        let count = 0, used = more.getBoundingClientRect().width + 4;
        while (count < choices.length && used + widths[count] + (count ? 4 : 0) <= availableWidth) {
          used += widths[count] + (count ? 4 : 0);
          count++;
        }
        count = Math.max(1, count);
        if (!expanded) choices.forEach((choice,index) => { choice.hidden = index >= count; });
        const hiddenCount = choices.length - count;
        more.textContent = expanded ? '−' : `+${hiddenCount}`;
        more.setAttribute('aria-expanded', String(expanded));
        more.setAttribute('aria-label', `${expanded ? 'Show fewer' : 'Show ' + hiddenCount + ' more'} ${group.dataset.optionName} options`);
      });
    }

    disconnectedCallback() {
      this.closeOverlay(false);
      this.mobileMedia?.removeEventListener('change', this.handleModeChange);
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.resizeFrame);
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeyDown);
      this.overlay?.removeEventListener('toggle', this.handleOverlayToggle);
      document.removeEventListener('pointerdown', this.handleOutsidePointer);
      document.removeEventListener('catalog-options:opened', this.handleOtherOverlay);
      document.removeEventListener('keydown', this.handleOverlayEscape);
      this.initialized = false;
    }
  }
  customElements.define('catalog-card-options', CatalogCardOptions);
}
